import { createSelector } from 'reselect';
import { selectTabByPath } from '.';
import { WebformState } from '@qodly/shared';
import { AppState } from '~store/modules';

export const ROOT_WEBFORM_STATE = { label: 'Base', id: 'root' };

export const isBaseState = (state: WebformState) =>
  state.id === ROOT_WEBFORM_STATE.id;

/**
 * Returns the list of declared states in the current tab.
 * @param tabPath the current tab path.
 * @param exclude states to exclude (by id)
 * @returns states or an empty array if not found.
 */
export const selectWebformStatesByPath = (
  tabPath: string,
  exclude: string[] = [],
) =>
  createSelector(selectTabByPath(tabPath), (tab): WebformState[] => {
    const initialStates = (tab?.content?.metadata?.states || [
      ROOT_WEBFORM_STATE,
    ]) as WebformState[];
    let states = initialStates;

    if (!states.find(isBaseState)) {
      states = [ROOT_WEBFORM_STATE, ...states];
    }

    return states.filter(({ id }) => !exclude.includes(id));
  });

/**
 * Returns the list of states labels in the current tab.
 * @param tabPath the current tab path.
 * @param exclude states to exclude (by id)
 * @returns states labels or an empty array if not found.
 */
export const selectWebformStatesLabelsByPath = (
  tabPath: string,
  exclude: string[] = [],
) =>
  createSelector(
    selectWebformStatesByPath(tabPath, exclude),
    (states): string[] => {
      return states.map(({ label }) => label);
    },
  );

/**
 * Returns the list of default states in the current tab.
 * @param tabPath the current tab path.
 * @returns default states ids array or an empty array if not found.
 */
export const selectDefaultWebformStatesByPath = (tabPath: string) =>
  createSelector(selectTabByPath(tabPath), (tab): string[] => {
    return tab?.content?.metadata?.defaultStates || [];
  });

export const selectCurrentWebformStateByPath = (tabPath: string) =>
  createSelector(selectTabByPath(tabPath), (tab): WebformState => {
    const { currentState } = tab?.content?.metadata || {};
    return (
      tab?.content?.metadata?.states?.find(
        (state: WebformState) => state.id === currentState,
      ) || ROOT_WEBFORM_STATE
    );
  });

/**
 * Returns the shared states conditions.
 */
export const selectSavedConditions = createSelector(
  (state: AppState) => state,
  (state) => {
    return state.savedConditions?.conditions || [];
  },
);
